import {useState, useEffect} from 'react'
import {Box, CircularProgress} from '@mui/material'
import {fetchS3File} from './shared'
import ProposalJsonViewer from './ProposalJsonViewer'

function sortKeys(obj: any): any {
    if (Array.isArray(obj)) return obj.map(sortKeys)
    if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).sort().reduce((acc: any, key: string) => {
            acc[key] = sortKeys(obj[key])
            return acc
        }, {})
    }
    return obj
}

function formatDollar(value: any): string {
    if (value == null) return 'N/A'
    return `$${Number(value).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
}

interface OriginalProposalTabProps {
    record: any
}

function OriginalProposalTab({record}: OriginalProposalTabProps) {
    const [data, setData] = useState<string | null>(null)
    const [rawData, setRawData] = useState<any>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const intakeURL = record?.data?.originalEvent?.meta?.intakeURL
    const s3Ref = intakeURL ? {bucketName: intakeURL.bucketName, key: `${intakeURL.key}/proposal.json`} : null

    useEffect(() => {
        if (!s3Ref?.bucketName || !s3Ref?.key) return
        setLoading(true)
        setError(null)
        fetchS3File(s3Ref)
            .then(result => {
                const sorted = sortKeys(result)
                setRawData(sorted)
                setData(JSON.stringify(sorted, null, 2))
            })
            .catch((err: any) => setError(err.message))
            .finally(() => setLoading(false))
    }, [s3Ref?.bucketName, s3Ref?.key])

    if (!s3Ref?.bucketName || !s3Ref?.key) {
        return <Box sx={{color: '#888', fontStyle: 'italic', p: 2}}>No original proposal S3 reference available</Box>
    }

    if (loading) {
        return (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, p: 2}}>
                <CircularProgress size={20}/>
                <span>Loading original proposal...</span>
            </Box>
        )
    }

    if (error) {
        return <Box sx={{color: 'red', p: 2}}>Error: {error}</Box>
    }

    if (!data) {
        return <Box sx={{color: '#888', fontStyle: 'italic', p: 2}}>No original proposal data</Box>
    }

    const quote = record?.data?.passThrough?.originalQuote

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', height: 'calc(90vh - 260px)'}}>
            {quote && (
                <Box sx={{display: 'flex', gap: 2, p: 1.5, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa'}}>
                    <Box sx={{px: 2, py: 1, borderRadius: 1, backgroundColor: '#e3f2fd', border: '1px solid #90caf9'}}>
                        <Box sx={{fontSize: '0.7rem', fontWeight: 600, color: '#1565c0', textTransform: 'uppercase', letterSpacing: '0.05em'}}>Premium</Box>
                        <Box sx={{fontSize: '1.1rem', fontWeight: 700, color: '#1976d2', fontFamily: 'monospace'}}>{formatDollar(quote.premium)}</Box>
                    </Box>
                    <Box sx={{px: 2, py: 1, borderRadius: 1, backgroundColor: '#e8f5e9', border: '1px solid #a5d6a7'}}>
                        <Box sx={{fontSize: '0.7rem', fontWeight: 600, color: '#2e7d32', textTransform: 'uppercase', letterSpacing: '0.05em'}}>Total Cost</Box>
                        <Box sx={{fontSize: '1.1rem', fontWeight: 700, color: '#388e3c', fontFamily: 'monospace'}}>{formatDollar(quote.totalCost)}</Box>
                    </Box>
                    <Box sx={{px: 2, py: 1, borderRadius: 1, backgroundColor: '#f3e5f5', border: '1px solid #ce93d8'}}>
                        <Box sx={{fontSize: '0.7rem', fontWeight: 600, color: '#7b1fa2', textTransform: 'uppercase', letterSpacing: '0.05em'}}>Installment Fees</Box>
                        <Box sx={{fontSize: '1.1rem', fontWeight: 700, color: '#9c27b0', fontFamily: 'monospace'}}>{formatDollar(quote.installmentFeesTotal)}</Box>
                    </Box>
                </Box>
            )}
            <ProposalJsonViewer data={data} rawData={rawData}/>
        </Box>
    )
}

export default OriginalProposalTab
